import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Animated, BackHandler, Dimensions, Modal, PanResponder, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, type TextInputProps, type ViewStyle, type TextStyle } from 'react-native';
import { useIsFocused } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { C, PALETA, alpha } from './theme';
import { t } from '../lib/i18n';
import { ScrollArrastavel } from './Arrastar';
import { Ditado } from './Ditado';
import { alternarRevelado, useDiscreto } from '../lib/discreto';
import { useCaixas } from '../lib/caixas';
import { abriuJanela } from '../lib/telasAbertas';
import { VoltarBorda } from './VoltarBorda';
import { BotaoInfo, LinkComoUsar } from './ComoUsarAba';
import type { AbaId } from '../store/types';
import { LARGURA_JANELA, useTamanho } from '../lib/tamanho';

/** 0.70 (texto 09, E): janela (Modal) e estado nunca ficam fora de sincronia.
 *  Causa do "matéria não abre": o iPhone às vezes fecha a janela por conta própria (ou nem chega a mostrar, quando
 *  outra janela está sendo apresentada) e o React continua achando que ela está aberta; aí tocar de novo não abre nada.
 *  Agora: se o iPhone avisa que fechou enquanto `visible` ainda é true, chama onClose (o estado volta a "fechada");
 *  se a janela não aparece em 2,5 s, tenta montar de novo uma vez e, se ainda não aparecer, chama onClose. */
export function useModalSeguro(visible: boolean, onClose: () => void) {
  const [chave, setChave] = useState(0);
  const st = useRef({ visible, onClose, chave, mostrou: false, tentou: 0, pediu: false, vivo: true });
  st.current.visible = visible; st.current.onClose = onClose; st.current.chave = chave;
  useEffect(() => () => { st.current.vivo = false; }, []);
  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    // o app pediu para fechar: o aviso de "fechou" que vem depois é esperado (mesmo se reabrir logo em seguida)
    if (!visible) { if (st.current.mostrou) st.current.pediu = true; st.current.mostrou = false; st.current.tentou = 0; return; }
    if (st.current.mostrou) return;
    const tm = setTimeout(() => {
      const s = st.current;
      if (s.mostrou || !s.visible) return;
      if (s.tentou < 1) { s.tentou++; setChave(k => k + 1); }
      else s.onClose();
    }, 2500);
    return () => clearTimeout(tm);
  }, [visible, chave]);
  return {
    key: 'm' + chave,
    onShow: () => { st.current.mostrou = true; },
    onDismiss: Platform.OS === 'ios' ? () => {
      const s = st.current, era = chave;
      if (s.pediu) { s.pediu = false; return; }
      s.mostrou = false;
      // fechou por conta própria (o estado ainda diz "aberta"), e não é a troca de chave da nova tentativa
      if (s.vivo && s.visible && era === s.chave) s.onClose();
    } : undefined,
  };
}

/** Puxar para baixo pelo cabeçalho fecha a folha (iPhone), como as janelas que sobem de baixo. */
function usePuxarParaBaixo(onClose: () => void, visible: boolean) {
  const y = useRef(new Animated.Value(0)).current;
  const fechar = useRef(onClose); fechar.current = onClose;
  const vis = useRef(visible); vis.current = visible;
  useEffect(() => { if (visible) y.setValue(0); }, [visible, y]);
  const pan = useMemo(() => PanResponder.create({
    onMoveShouldSetPanResponderCapture: (_, g) => g.dy > 8 && g.dy > Math.abs(g.dx) * 1.5,
    onMoveShouldSetPanResponder: (_, g) => g.dy > 8 && g.dy > Math.abs(g.dx) * 1.5,
    onPanResponderTerminationRequest: () => false,
    onPanResponderMove: (_, g) => y.setValue(Math.max(0, g.dy)),
    onPanResponderRelease: (_, g) => {
      const H = Dimensions.get('window').height;
      if (g.dy > H * 0.25 || (g.vy > 0.6 && g.dy > 30)) {
        Animated.timing(y, { toValue: H, duration: 200, useNativeDriver: true }).start(() => {
          fechar.current();
          // continuou aberta (ex.: página > matéria): volta para o lugar; fechou: fica fora até abrir de novo
          setTimeout(() => { if (vis.current) y.setValue(0); }, 80);
        });
      } else Animated.spring(y, { toValue: 0, useNativeDriver: true, bounciness: 0, speed: 18 }).start();
    },
    onPanResponderTerminate: () => Animated.spring(y, { toValue: 0, useNativeDriver: true, bounciness: 0, speed: 18 }).start(),
  }), [y]);
  return { y, pan: Platform.OS === 'ios' ? pan.panHandlers : {} };
}

export const T = ({ children, s, n }: { children: ReactNode; s?: TextStyle | TextStyle[]; n?: number }) =>
  <Text numberOfLines={n} style={[{ color: C.text, fontSize: 15 }, s as any]}>{children}</Text>;
export const Sub = ({ children, s }: { children: ReactNode; s?: TextStyle }) =>
  <Text style={[{ color: C.sub, fontSize: 12 }, s]}>{children}</Text>;

export function Chip({ on, label, onPress, cor, small }: { on: boolean; label: string; onPress: () => void; cor: string; small?: boolean }) {
  return (
    <Pressable onPress={onPress} style={{ paddingVertical: small ? 6 : 8, paddingHorizontal: small ? 10 : 13, borderRadius: 18,
      backgroundColor: on ? cor : C.card2, borderWidth: 1, borderColor: on ? cor : C.line }}>
      <Text style={{ color: on ? '#fff' : C.text, fontSize: small ? 12 : 13, fontWeight: on ? '600' : '400' }}>{label}</Text>
    </Pressable>
  );
}

export function Btn({ label, onPress, cor, outline, disabled, style }:
  { label: string; onPress: () => void; cor: string; outline?: boolean; disabled?: boolean; style?: ViewStyle }) {
  return (
    <Pressable disabled={disabled} onPress={onPress} style={({ pressed }) => [{
      backgroundColor: outline ? 'transparent' : disabled ? C.mute : cor, borderWidth: 1, borderColor: outline ? cor : 'transparent',
      paddingVertical: 14, paddingHorizontal: 16, borderRadius: 14, opacity: pressed ? 0.8 : 1 }, style]}>
      <Text style={{ color: outline ? cor : '#fff', textAlign: 'center', fontWeight: '700', fontSize: 15 }}>{label}</Text>
    </Pressable>
  );
}

/** Campo de texto. Campos de várias linhas ganham o 🎤 (ditado) no canto (0.65, B10); semDitado tira. */
export function Field(props: TextInputProps & { semDitado?: boolean; corDitado?: string }) {
  if (props.secureTextEntry) return <CampoSenha {...props} />;
  return <FieldTexto {...props} />;
}

/** Campo de senha (0.66, B5): olhinho 👁️ para ver o que está digitando e aviso de Caps Lock
 *  (teclado bluetooth: duas letras maiúsculas seguidas acendem o aviso). */
export function CampoSenha({ semDitado: _s, corDitado: _c, style, ...p }: TextInputProps & { semDitado?: boolean; corDitado?: string }) {
  const [ver, setVer] = useState(false);
  const v = typeof p.value === 'string' ? p.value : '';
  const caps = /[A-ZÀ-Ý]{2}$/.test(v) && p.keyboardType !== 'number-pad' && p.keyboardType !== 'numeric';
  return (
    <View style={style as any}>
      <View>
        <TextInput placeholderTextColor={C.mute} selectionColor="#8B5CF6" autoCapitalize="none" autoCorrect={false} inputAccessoryViewButtonLabel={t('Concluído')} {...p} secureTextEntry={!ver}
          style={{ backgroundColor: C.card2, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 12, paddingRight: 48, color: C.text, fontSize: 15 }} />
        <Pressable onPress={() => setVer(x => !x)} hitSlop={8} accessibilityLabel={ver ? t('Esconder a senha') : t('Mostrar a senha')}
          style={{ position: 'absolute', right: 6, top: 0, bottom: 0, width: 38, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ fontSize: 18, opacity: ver ? 1 : 0.55 }}>{ver ? '🙈' : '👁️'}</Text>
        </Pressable>
      </View>
      {caps && <Text style={{ color: C.warn, fontSize: 12, marginTop: 4 }}>⇪ {t('O Caps Lock parece estar ligado.')}</Text>}
    </View>
  );
}

function FieldTexto({ semDitado, corDitado, ...p }: TextInputProps & { semDitado?: boolean; corDitado?: string }) {
  // texto 18 B4: teclados só de números não têm a tecla de fechar no iPhone: ganham o botão "Concluído" em cima
  const campo = <TextInput placeholderTextColor={C.mute} selectionColor="#8B5CF6" inputAccessoryViewButtonLabel={t('Concluído')} {...p}
    style={[{ backgroundColor: C.card2, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 12, color: C.text, fontSize: 15 }, p.multiline && !semDitado ? { paddingRight: 46 } : null, p.style]} />;
  if (!p.multiline || semDitado || p.editable === false || !p.onChangeText || typeof p.value !== 'string') return campo;
  const valor = p.value, mudar = p.onChangeText;
  return (
    <View>
      {campo}
      <View style={{ position: 'absolute', right: 6, top: 8 }}>
        <Ditado pequeno cor={corDitado ?? '#8B5CF6'} onTexto={tx => mudar(valor && !/\s$/.test(valor) ? valor + ' ' + tx : valor + tx)} />
      </View>
    </View>
  );
}

/** Texto longo mostrado em 2 linhas, com "…ver mais" para abrir o resto (0.65, Texto 3 A). */
export function TextoMais({ texto, linhas = 2, style, cor }: { texto: string; linhas?: number; style?: TextStyle; cor?: string }) {
  const [aberto, setAberto] = useState(false);
  const [corta, setCorta] = useState(false);
  if (!texto) return null;
  return (
    <View>
      <Text style={[{ color: C.sub, fontSize: 13 }, style]} numberOfLines={aberto ? undefined : linhas}
        onTextLayout={e => { if (!aberto) setCorta(e.nativeEvent.lines.length > linhas || texto.split('\n').length > linhas); }}>{texto}</Text>
      {(corta || texto.length > 140) && (
        <Pressable onPress={() => setAberto(a => !a)} hitSlop={6}>
          <Text style={{ color: cor ?? C.sub, fontSize: 12, fontWeight: '700' }}>{aberto ? t('ver menos') : '…' + t('ver mais')}</Text>
        </Pressable>
      )}
    </View>
  );
}

/** Linha da ficha: mostra o valor escolhido e abre as opções ao tocar. */
export function Row({ icon, label, value, open, onPress, children, right }:
  { icon: string; label: string; value: string; open: boolean; onPress: () => void; children?: ReactNode; right?: ReactNode }) {
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 14, overflow: 'hidden' }}>
      <Pressable onPress={onPress} style={{ flexDirection: 'row', alignItems: 'center', padding: 14, gap: 12 }}>
        <Text style={{ fontSize: 18, width: 26, textAlign: 'center' }}>{icon}</Text>
        <Text style={{ color: C.sub, fontSize: 14, width: 86 }}>{label}</Text>
        <Text style={{ color: C.text, fontSize: 14, flex: 1, textAlign: 'right' }} numberOfLines={2}>{value}</Text>
        {right ?? <Text style={{ color: C.mute }}>{open ? '▴' : '▾'}</Text>}
      </Pressable>
      {open && <View style={{ paddingHorizontal: 14, paddingBottom: 14, gap: 10 }}>{children}</View>}
    </View>
  );
}

/** Folha que sobe de baixo (tela cheia), com cabeçalho.
 *  0.70 (texto 09, E): no iPhone, puxar da borda esquerda volta (mesmo que "Fechar": na matéria, página > matéria > fecha)
 *  e puxar o cabeçalho para baixo fecha. No iPhone a janela é "overFullScreen" (transparent) para a tela de baixo aparecer
 *  enquanto o dedo puxa; parada, fica igual (o fundo da folha cobre tudo). */
export function Sheet({ visible, onClose, title, children, right, larga }:
  { visible: boolean; onClose: () => void; title: string; children: ReactNode; right?: ReactNode;
    /** texto 16 C: no computador, janela larga (relatórios), em vez da janela de formulário */
    larga?: boolean }) {
  const ins = useSafeAreaInsets();
  useEffect(() => (visible ? abriuJanela() : undefined), [visible]);   // 0.69 (D): os avisos da raiz esperam a janela fechar
  const seguro = useModalSeguro(visible, onClose);
  const baixo = usePuxarParaBaixo(onClose, visible);
  const ios = Platform.OS === 'ios';
  const tam = useTamanho();
  // Texto 12 B: no navegador largo (tablet e computador), formulários e janelas abrem numa janela central de 560 px,
  // com o fundo escurecido (clicar fora fecha), e não esticados na tela inteira.
  if (tam !== 'celular') return (
    <Modal key={seguro.key} visible={visible} transparent animationType="fade" onRequestClose={onClose} onShow={seguro.onShow} onDismiss={seguro.onDismiss}>
      <View style={{ flex: 1, backgroundColor: C.overlay, alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Pressable onPress={onClose} accessibilityLabel={t('Fechar')} style={StyleSheet.absoluteFill} />
        <View style={{ width: '100%', maxWidth: larga && tam === 'computador' ? 900 : LARGURA_JANELA, height: '100%', maxHeight: 820, backgroundColor: C.bg, borderRadius: 18, overflow: 'hidden',
          borderWidth: 1, borderColor: C.line, shadowColor: '#000', shadowOpacity: 0.35, shadowRadius: 24 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: C.line }}>
            <Pressable onPress={onClose} hitSlop={12}><Text style={{ color: C.sub, fontSize: 15 }}>{t('Fechar')}</Text></Pressable>
            <Text style={{ flex: 1, textAlign: 'center', color: C.text, fontWeight: '700', fontSize: 16 }}>{title}</Text>
            <View style={{ minWidth: 50, alignItems: 'flex-end' }}>{right}</View>
          </View>
          {children}
        </View>
      </View>
    </Modal>
  );
  return (
    <Modal key={seguro.key} visible={visible} animationType="slide" onRequestClose={onClose} onShow={seguro.onShow} onDismiss={seguro.onDismiss}
      transparent={ios} presentationStyle={ios ? 'overFullScreen' : 'fullScreen'}>
      <VoltarBorda onVoltar={onClose} ativo={visible} janela>
      <Animated.View style={{ flex: 1, backgroundColor: C.bg, paddingTop: ins.top + 6, transform: [{ translateY: baixo.y }] }}>
        <View {...baixo.pan} style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10 }}>
          <Pressable onPress={onClose} hitSlop={12}><Text style={{ color: C.sub, fontSize: 15 }}>{t('Fechar')}</Text></Pressable>
          <Text style={{ flex: 1, textAlign: 'center', color: C.text, fontWeight: '700', fontSize: 16 }}>{title}</Text>
          <View style={{ minWidth: 50, alignItems: 'flex-end' }}>{right}</View>
        </View>
        {children}
      </Animated.View>
      </VoltarBorda>
    </Modal>
  );
}

/** Menu de opções que sobe de baixo. */
export type Action = { label: string; onPress: () => void; destructive?: boolean };
export function ActionSheet({ title, actions, onClose }: { title: string; actions: Action[] | null; onClose: () => void }) {
  const ins = useSafeAreaInsets();
  const largo = useTamanho() !== 'celular';
  const seguro = useModalSeguro(!!actions, onClose);
  return (
    <Modal key={seguro.key} visible={!!actions} transparent animationType="fade" onRequestClose={onClose} onShow={seguro.onShow} onDismiss={seguro.onDismiss}>
      <Pressable onPress={onClose} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'flex-end' }}>
        <Pressable style={{ backgroundColor: C.card, borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 16, paddingBottom: ins.bottom + 16, gap: 2,
          ...(largo ? { alignSelf: 'center', width: '100%', maxWidth: LARGURA_JANELA } : null) }}>
          <Text style={{ color: C.text, fontWeight: '700', fontSize: 16, marginBottom: 8 }} numberOfLines={2}>{title}</Text>
          <ScrollView style={{ maxHeight: Math.round(Dimensions.get('window').height * 0.62) }} bounces={false}>
          {(actions ?? []).map(a => (
            <Pressable key={a.label} onPress={() => { onClose(); setTimeout(a.onPress, 250); }}
              style={({ pressed }) => ({ paddingVertical: 14, paddingHorizontal: 8, borderRadius: 10, backgroundColor: pressed ? C.card2 : 'transparent' })}>
              <Text style={{ fontSize: 16, color: a.destructive ? C.danger : C.text }}>{a.label}</Text>
            </Pressable>
          ))}
          </ScrollView>
          <Pressable onPress={onClose} style={{ paddingVertical: 14, paddingHorizontal: 8 }}><Text style={{ fontSize: 16, color: C.sub }}>{t('Cancelar')}</Text></Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** Tela com título. Com onBack: "‹" no topo e gesto da borda esquerda no iPhone (0.70, texto 09, E).
 *  O "voltar" do Android NÃO é ligado aqui de propósito: na tela raiz de uma aba o onBack navega para o My Planner
 *  quando não há histórico, e o voltar do sistema ficaria indo e vindo sem nunca sair do app. Subpáginas usam useVoltarAndroid. */
export function Screen({ title, right, children, scroll = true, onBack, onGesto, rolagem: Rolagem = ScrollArrastavel, ajuda }:
  { title: string; right?: ReactNode; children: ReactNode; scroll?: boolean; onBack?: () => void;
    /** texto 28 (B6/B7): a aba desta tela: ⓘ no topo e "Como usar esta aba" no fim (quando o Guia tem a página dela) */
    ajuda?: AbaId;
    /** a rolagem da tela (ex.: Meu Dia usa a PaginaRolavel para a barra do editor ficar fixa no topo) */
    rolagem?: ComponentType<any>;
    /** 0.72 (texto 11a): só o gesto da borda (sem o "‹"), na raiz das abas fixas: volta ao My Planner. */
    onGesto?: () => void }) {
  const gesto = onBack ?? onGesto;
  const ins = useSafeAreaInsets();
  const cx = useCaixas();   // "Tamanho das caixas": espaço entre os blocos da tela (Grande = 10, como antes)
  const head = (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 6 }}>
      {onBack && <Pressable onPress={onBack} hitSlop={12}><Text style={{ color: C.sub, fontSize: 22 }}>‹</Text></Pressable>}
      <Text style={{ color: C.text, fontSize: 26, fontWeight: '800', flex: 1 }}>{title}</Text>
      <OlhoDiscreto />
      {!!ajuda && <BotaoInfo aba={ajuda} />}
      {right}
    </View>
  );
  if (!scroll) return (
    <VoltarBorda onVoltar={gesto} ativo={!!gesto}>
      <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: ins.top + 10, paddingHorizontal: 16 }}>{head}{children}</View>
    </VoltarBorda>
  );
  return (
    <VoltarBorda onVoltar={gesto} ativo={!!gesto}>
    <Rolagem style={{ flex: 1, backgroundColor: C.bg }} keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ paddingTop: ins.top + 10, paddingHorizontal: 16, paddingBottom: 120, gap: cx.gap(10) }}>
      {head}{children}
      {!!ajuda && <LinkComoUsar aba={ajuda} />}
    </Rolagem>
    </VoltarBorda>
  );
}

/** Android: o "voltar" do sistema faz o mesmo que o "‹" de uma subpágina (só com a aba em foco; janelas Modal têm o
 *  próprio onRequestClose). O último registrado responde primeiro. Passe undefined quando não estiver numa subpágina. */
export function useVoltarAndroid(onBack?: () => void) {
  const foco = useIsFocused();
  const ref = useRef(onBack); ref.current = onBack;
  const tem = !!onBack;
  useEffect(() => {
    if (Platform.OS !== 'android' || !foco || !tem) return;
    const s = BackHandler.addEventListener('hardwareBackPress', () => { if (!ref.current) return false; ref.current(); return true; });
    return () => s.remove();
  }, [foco, tem]);
}

/** Modo discreto (0.67): 👁️ no topo das telas mostra ou esconde os valores em dinheiro. */
function OlhoDiscreto() {
  const d = useDiscreto();
  if (!d.ligado) return null;
  return (
    <Pressable onPress={alternarRevelado} hitSlop={8} accessibilityLabel={d.revelado ? t('Esconder os valores') : t('Mostrar os valores')}
      style={{ width: 38, height: 38, borderRadius: 19, backgroundColor: C.card, alignItems: 'center', justifyContent: 'center' }}>
      <Text style={{ fontSize: 17 }}>{d.revelado ? '🙈' : '👁️'}</Text>
    </Pressable>
  );
}

export function Section({ t, cor }: { t: string; cor?: string }) {
  return <Text style={{ color: cor ?? C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 14 }}>{t.toUpperCase()}</Text>;
}

export function Swatches({ value, onChange, extra }: { value: string | null; onChange: (c: string) => void; extra?: ReactNode }) {
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
      {PALETA.map(c => (
        <Pressable key={c} onPress={() => onChange(c)} style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: c,
          borderWidth: value === c ? 3 : 0, borderColor: '#fff' }} />
      ))}
      {extra}
    </View>
  );
}

export const tint = (c: string) => ({ backgroundColor: alpha(c, 0.14), borderColor: alpha(c, 0.35) });
